import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import ClientShell from "./ClientShell";
import { RatingRow } from "./review/RatingRow";
import { QuestionField } from "./review/QuestionField";
import { VideoAttach } from "./review/VideoAttach";
import { ConsentStep } from "./review/ConsentStep";
import { useReviewDraft, useSaveReviewDraft, useSubmitReview, useReviewHistory } from "@/lib/reviewQueries";

const QUESTIONS = [
  { key: "challenge", label: "What challenge were you facing?" },
  { key: "collaboration", label: "How did we work together?" },
  { key: "outcome", label: "What outcome did you achieve?" },
  { key: "outcome_link", label: "How did the work help you reach that outcome?" },
  { key: "most_important", label: "What was the most important part of the journey?" },
  { key: "what_would_change", label: "What would you change about the programme or coach if you started again?" },
];

/**
 * Master v5 Prompt 15 — Programme/coach review and testimonial consent.
 * Reachable from the persistent ClientShell "Review programme & coach"
 * action and from the Home invitation card. Deliberately separate from
 * coaching Feedback (FeedbackPage.jsx) — different collections, different
 * privacy rule (member-private, never team-visible, never auto-published).
 */
export default function ReviewPage() {
  const { workspaceId } = useParams();
  const { data: draftData, isPending: draftPending } = useReviewDraft(workspaceId);
  const { data: history } = useReviewHistory(workspaceId);
  const saveDraft = useSaveReviewDraft(workspaceId);
  const submitReview = useSubmitReview(workspaceId);
  const [justSubmittedRsbId, setJustSubmittedRsbId] = useState(null);
  const [expandedRsbId, setExpandedRsbId] = useState(null);
  const [managingConsentFor, setManagingConsentFor] = useState(null);
  const [submitError, setSubmitError] = useState(null);

  const [form, setForm] = useState({ coach_rating: null, programme_rating: null, video_fas_id: null, answers: {} });
  const saveTimer = useRef(null);
  const hydrated = useRef(false);

  useEffect(() => {
    if (hydrated.current) return;
    if (draftData?.submission) {
      setForm({
        coach_rating: draftData.submission.coach_rating, programme_rating: draftData.submission.programme_rating,
        video_fas_id: draftData.submission.video_fas_id, answers: draftData.answers,
      });
      hydrated.current = true;
    } else if (draftData && !draftData.submission) {
      // First visit / no active draft — eagerly create one so an rsb_id
      // is immediately available (needed for the optional video attach).
      hydrated.current = true;
      saveDraft.mutate({ answers: {} });
    }
  }, [draftData]);

  function scheduleSave(next) {
    setForm(next);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => saveDraft.mutate(next), 600);
  }

  function startNewDraft() {
    hydrated.current = true;
    setForm({ coach_rating: null, programme_rating: null, video_fas_id: null, answers: {} });
    setJustSubmittedRsbId(null);
    setSubmitError(null);
    saveDraft.mutate({ coach_rating: null, programme_rating: null, answers: {} });
  }

  async function handleSubmit() {
    setSubmitError(null);
    try {
      const result = await submitReview.mutateAsync();
      setJustSubmittedRsbId(result.submission.rsb_id);
    } catch (err) {
      setSubmitError(
        err.status === 403
          ? "You're viewing as this client — reviews can only be submitted by the client themselves."
          : "Couldn't submit your review. Please try again.",
      );
    }
  }

  const rsbId = draftData?.submission?.rsb_id;
  const isComplete = form.coach_rating && form.programme_rating && QUESTIONS.every((q) => (form.answers[q.key] || "").trim().length > 0);

  if (draftPending) {
    return <ClientShell><p className="text-sm text-ink-soft" data-testid="review-loading">Loading…</p></ClientShell>;
  }

  // Just submitted — show the separate, unselected consent step for THIS submission.
  if (justSubmittedRsbId) {
    return (
      <ClientShell>
        <div className="max-w-2xl" data-testid="review-page">
          <p className="text-sm text-terracotta font-medium mb-4" data-testid="review-just-submitted-banner">Your review has been submitted — thank you.</p>
          <ConsentStep workspaceId={workspaceId} rsbId={justSubmittedRsbId} hasVideo={!!form.video_fas_id} currentConsent={null} />
          <button onClick={startNewDraft} className="mt-6 text-sm text-terracotta hover:underline" data-testid="review-write-another-button">
            Write another review
          </button>
        </div>
      </ClientShell>
    );
  }

  // Managing consent for a past submission from history.
  if (managingConsentFor) {
    return (
      <ClientShell>
        <div className="max-w-2xl" data-testid="review-page">
          <button onClick={() => setManagingConsentFor(null)} className="text-sm text-ink-soft hover:text-ink mb-4" data-testid="review-consent-manage-back">&larr; Back</button>
          <ConsentStep
            workspaceId={workspaceId} rsbId={managingConsentFor.submission.rsb_id}
            hasVideo={!!managingConsentFor.submission.video_fas_id} currentConsent={managingConsentFor.consent}
          />
        </div>
      </ClientShell>
    );
  }

  return (
    <ClientShell>
      <div className="max-w-2xl" data-testid="review-page">
        <h1 className="font-serif text-2xl text-ink mb-1">Review your programme &amp; coach</h1>
        <p className="text-sm text-ink-soft mb-6">Your review is private to you. Your coach may read it, but nothing is ever published automatically.</p>

        <div className="border border-ink/15 bg-white/50 rounded-lg p-5 mb-6" data-testid="review-form">
          <RatingRow label="How would you rate your coach?" value={form.coach_rating} onChange={(v) => scheduleSave({ ...form, coach_rating: v })} testId="review-coach-rating" />
          <RatingRow label="How would you rate the programme?" value={form.programme_rating} onChange={(v) => scheduleSave({ ...form, programme_rating: v })} testId="review-programme-rating" />

          <div className="h-px bg-ink/10 my-4" />

          {QUESTIONS.map((q) => (
            <QuestionField
              key={q.key} questionKey={q.key} label={q.label} value={form.answers[q.key]}
              onChange={(k, v) => scheduleSave({ ...form, answers: { ...form.answers, [k]: v } })}
              testId={`review-question-${q.key}`}
            />
          ))}

          <VideoAttach
            workspaceId={workspaceId} rsbId={rsbId} videoFasId={form.video_fas_id}
            onAttached={(fasId) => scheduleSave({ ...form, video_fas_id: fasId })}
            onRemove={() => scheduleSave({ ...form, video_fas_id: null })}
          />

          <p className="text-xs text-ink-soft mb-3" data-testid="review-autosave-indicator">
            {saveDraft.isPending ? "Saving…" : saveDraft.isError ? "Couldn't save just now — check your connection." : "Draft saved — you can come back and finish this anytime."}
          </p>

          {submitError && <p className="text-sm text-red-600 mb-3" data-testid="review-submit-error">{submitError}</p>}

          <button
            onClick={handleSubmit}
            disabled={!isComplete || submitReview.isPending}
            className="px-5 py-2.5 rounded-lg text-sm font-medium bg-terracotta text-white disabled:opacity-40"
            data-testid="review-submit-button"
          >
            {submitReview.isPending ? "Submitting…" : "Submit review"}
          </button>
        </div>

        {history?.items?.length > 0 && (
          <div data-testid="review-history">
            <p className="text-sm uppercase tracking-wide text-ink-soft mb-3">Your past reviews</p>
            <div className="space-y-2">
              {history.items.map((item) => {
                const isOpen = expandedRsbId === item.submission.rsb_id;
                return (
                  <div key={item.submission.rsb_id} className="border border-ink/15 rounded-lg bg-white/30" data-testid={`review-history-item-${item.submission.rsb_id}`}>
                    <button
                      onClick={() => setExpandedRsbId(isOpen ? null : item.submission.rsb_id)}
                      className="w-full text-left px-4 py-3"
                      data-testid={`review-history-item-toggle-${item.submission.rsb_id}`}
                    >
                      <p className="text-sm text-ink">Submitted {new Date(item.submission.submitted_at).toLocaleString()}</p>
                      <p className="text-xs text-ink-soft">Testimonial consent: {item.consent?.consent_status || "not yet decided"} — click to view</p>
                    </button>
                    {isOpen && (
                      <div className="px-4 pb-4 space-y-2" data-testid={`review-history-item-detail-${item.submission.rsb_id}`}>
                        <p className="text-sm text-ink-soft">Coach rating: {item.submission.coach_rating}/5 · Programme rating: {item.submission.programme_rating}/5</p>
                        {QUESTIONS.map((q) => (
                          <div key={q.key}>
                            <p className="text-xs text-ink-soft">{q.label}</p>
                            <p className="text-sm text-ink">{item.answers[q.key]}</p>
                          </div>
                        ))}
                        <button
                          onClick={() => setManagingConsentFor(item)}
                          className="text-sm text-terracotta hover:underline"
                          data-testid={`review-history-manage-consent-${item.submission.rsb_id}`}
                        >
                          Manage testimonial consent
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </ClientShell>
  );
}
